package br.com.texeratx.boardgametimeapp.ui.screens.stats

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.data.model.stats.CategoryStatsDTO
import br.com.texeratx.boardgametimeapp.data.model.stats.PlayerMinutesDTO
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppLoadingOverlay
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.utils.minutesToHourMin
import br.com.texeratx.boardgametimeapp.ui.utils.minutesToHourMinCompact
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StatsUI(
    state: StatsUiState,
    onBack: () -> Unit,
    onSelectTab: (StatsTab) -> Unit,
    onSetYear: (Int) -> Unit,
) {
    val S = LocalStrings.current
    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.statsTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors()
            )
        }
    ) { padding ->

        Box(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
        ) {
            Column(modifier = Modifier.fillMaxSize()) {

                TabRow(selectedTabIndex = state.selectedTab.ordinal) {
                    Tab(
                        selected = state.selectedTab == StatsTab.Global,
                        onClick = { onSelectTab(StatsTab.Global) },
                        text = { Text(S.tabGlobal) }
                    )
                    Tab(
                        selected = state.selectedTab == StatsTab.Year,
                        onClick = { onSelectTab(StatsTab.Year) },
                        text = { Text(S.tabYear) }
                    )
                    Tab(
                        selected = state.selectedTab == StatsTab.Duration,
                        onClick = { onSelectTab(StatsTab.Duration) },
                        text = { Text(S.tabDuration) }
                    )
                }

                // Conteúdo rolável por aba
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {

                    // erro inline (sem dialog, simples)
                    state.error?.let { msg ->
                        Text(
                            text = msg,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }

                    when (state.selectedTab) {
                        StatsTab.Global -> GlobalTab(state)
                        StatsTab.Year -> YearTab(state, onSetYear)
                        StatsTab.Duration -> DurationTab(state)
                    }

                    Spacer(Modifier.height(24.dp))
                }
            }

            // overlay de loading (igual dashboard)
            AppLoadingOverlay(isVisible = state.isLoading)
        }
    }
}

@Composable
private fun GlobalTab(state: StatsUiState) {
    val S = LocalStrings.current
    val global = state.global ?: run {
        Text(S.loading)
        return
    }

    // ── Resumo ──
    SectionTitle(S.sectionSummary)
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        if (global.totalMatches > 0) {
            StatCardSmall(
                title = S.statMatches,
                value = global.totalMatches.toString(),
                modifier = Modifier.weight(1f)
            )
        }
        StatCardSmall(
            title = S.totalTimePlayed,
            value = minutesToHourMinCompact(global.totalMinutes),
            modifier = Modifier.weight(1f)
        )
        if (global.uniqueGamesCount > 0) {
            StatCardSmall(
                title = S.uniqueGamesLabel,
                value = global.uniqueGamesCount.toString(),
                modifier = Modifier.weight(1f)
            )
        }
    }

    // ── Ranking (winrate) ──
    CollapsibleSection(S.sectionRankingWinrate) {
        Card(shape = RoundedCornerShape(16.dp)) {
            Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                global.playerStats
                    .sortedByDescending { it.winRate }
                    .take(10)
                    .forEach { p ->
                        val pct = (p.winRate * 100).toInt()
                        PlayerListRow(
                            name = p.name,
                            nickname = p.nickname,
                            imageUrl = p.imageUrl,
                            trailing = S.rankingWinrateFormat(pct, p.wins),
                            trailing2 = S.participationsFormat(p.participations)
                        )
                    }
            }
        }
    }

    // ── Participações ──
    if (global.playerParticipationRanking.isNotEmpty()) {
        CollapsibleSection(S.sectionParticipations) {
            Card(shape = RoundedCornerShape(16.dp)) {
                Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    global.playerParticipationRanking.take(10).forEach { p ->
                        PlayerListRow(
                            name = p.name,
                            nickname = p.nickname,
                            imageUrl = p.imageUrl,
                            trailing = S.participationsCountFormat(p.count)
                        )
                    }
                }
            }
        }
    }

    // ── Tempo por jogador ──
    if (global.playerMinutesRanking.isNotEmpty()) {
        CollapsibleSection(S.sectionPlayerMinutes) {
            PlayerMinutesCard(global.playerMinutesRanking)
        }
    }

    // ── Jogos mais jogados ──
    CollapsibleSection(S.sectionMostPlayedGames) {
        GamesMostPlayedCard(global.gamesMostPlayed)
    }

    // ── Categorias ──
    if (global.categoriesMostPlayed.isNotEmpty()) {
        CollapsibleSection(S.sectionCategoriesMostPlayed) {
            CategoriesCard(global.categoriesMostPlayed)
        }
    }
}

@Composable
private fun YearTab(
    state: StatsUiState,
    onSetYear: (Int) -> Unit
) {
    val S = LocalStrings.current

    // selector de ano (não recolhível — é navegação)
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        OutlinedButton(onClick = { onSetYear(state.selectedYear - 1) }, modifier = Modifier.weight(1f)) {
            Text("← ${state.selectedYear - 1}")
        }
        OutlinedButton(onClick = { onSetYear(state.selectedYear + 1) }, modifier = Modifier.weight(1f)) {
            Text("${state.selectedYear + 1} →")
        }
    }

    val y = state.yearHistory ?: run {
        Text(S.loadingYearHistory)
        return
    }

    // ── Resumo ──
    SectionTitle(S.sectionSummary)
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        StatCardSmall(
            title = S.statMatches,
            value = y.totalMatches.toString(),
            modifier = Modifier.weight(1f)
        )
        StatCardSmall(
            title = S.statTimePlayedLabel,
            value = minutesToHourMinCompact(y.totalMinutes),
            modifier = Modifier.weight(1f)
        )
        if (y.uniqueGamesCount > 0) {
            StatCardSmall(
                title = S.uniqueGamesLabel,
                value = y.uniqueGamesCount.toString(),
                modifier = Modifier.weight(1f)
            )
        }
    }

    // ── Vitórias no ano ──
    CollapsibleSection(S.winsYear) {
        Card(shape = RoundedCornerShape(16.dp)) {
            Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                y.winsByPlayer
                    .sortedByDescending { it.winRate }
                    .take(10)
                    .forEach { p ->
                        val displayName = p.nickname?.takeIf { it.isNotBlank() } ?: p.name
                        val winRatePct = (p.winRate * 100).toInt()
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(modifier = Modifier.weight(1f), verticalAlignment = Alignment.CenterVertically) {
                                PlayerAvatar(imageUrl = p.imageUrl, contentDescription = displayName, size = 34.dp)
                                Spacer(Modifier.width(10.dp))
                                Column {
                                    Text(displayName, fontWeight = FontWeight.SemiBold)
                                    if (p.nickname != null && p.nickname!!.isNotBlank()) {
                                        Text(p.name, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                    }
                                }
                            }
                            Spacer(Modifier.width(12.dp))
                            Column(horizontalAlignment = Alignment.End) {
                                Text(S.winratePercentFormat(winRatePct), fontWeight = FontWeight.SemiBold, color = MaterialTheme.colorScheme.primary)
                                Text(S.winsCountFormat(p.wins), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            }
                        }
                    }
            }
        }
    }

    // ── Tempo por jogador ──
    if (y.playerMinutesRanking.isNotEmpty()) {
        CollapsibleSection(S.sectionPlayerMinutes) {
            PlayerMinutesCard(y.playerMinutesRanking)
        }
    }

    // ── Partidas por mês ──
    CollapsibleSection(S.matchesPerMonth) {
        MatchesPerMonthChart(matchesPerMonth = y.matchesPerMonth)
    }

    // ── Jogos mais jogados ──
    CollapsibleSection(S.mostPlayedYear) {
        GamesMostPlayedCard(y.mostPlayedGames)
    }

    // ── Categorias ──
    if (y.categoriesMostPlayed.isNotEmpty()) {
        CollapsibleSection(S.sectionCategoriesMostPlayed) {
            CategoriesCard(y.categoriesMostPlayed)
        }
    }
}

@Composable
private fun DurationTab(state: StatsUiState) {
    val S = LocalStrings.current
    val list = state.durationStats
    if (list.isEmpty()) {
        Text(S.loadingDurationStats)
        return
    }

    SectionTitle(S.durationPerGame)
    Card(shape = RoundedCornerShape(16.dp)) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            list.sortedByDescending { it.count }
                .take(30)
                .forEach { g ->
                    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                        Text(g.name, fontWeight = FontWeight.SemiBold)
                        Text(
                            S.gameDurationStatsFormat(g.count, g.avgDuration.toInt(), g.minDuration, g.maxDuration),
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
        }
    }
}

@Composable
private fun CollapsibleSection(
    title: String,
    initiallyExpanded: Boolean = true,
    content: @Composable () -> Unit
) {
    var expanded by remember { mutableStateOf(initiallyExpanded) }
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { expanded = !expanded }
                .padding(vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
            Icon(
                imageVector = if (expanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        AnimatedVisibility(visible = expanded) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                content()
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold
    )
}

@Composable
private fun StatBigCard(title: String, value: String) {
    Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(value, style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Bold)
            Text(title, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun StatCardSmall(title: String, value: String, modifier: Modifier = Modifier) {
    Card(shape = RoundedCornerShape(16.dp), modifier = modifier.height(86.dp)) {
        Column(
            Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(value, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, maxLines = 1)
            Text(title, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1)
        }
    }
}

@Composable
private fun MonthBarsCard(matchesPerMonth: Map<Int, Int>) {
    val S = LocalStrings.current
    val max = (matchesPerMonth.values.maxOrNull() ?: 0).coerceAtLeast(1)

    Card(shape = RoundedCornerShape(16.dp)) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            (1..12).forEach { month ->
                val value = matchesPerMonth[month] ?: 0
                MonthBarRow(
                    month = month,
                    value = value,
                    fraction = value.toFloat() / max.toFloat()
                )
            }
        }
    }
}

@Composable
private fun MonthBarRow(month: Int, value: Int, fraction: Float) {
    val S = LocalStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(S.monthLabel(month), style = MaterialTheme.typography.bodyMedium)
            Text(
                value.toString(),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        // barra
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(10.dp)
                .background(
                    color = MaterialTheme.colorScheme.surfaceVariant,
                    shape = RoundedCornerShape(999.dp)
                )
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(fraction.coerceIn(0f, 1f))
                    .height(10.dp)
                    .background(
                        color = MaterialTheme.colorScheme.primary,
                        shape = RoundedCornerShape(999.dp)
                    )
            )
        }
    }
}

@Composable
private fun GameListRow(
    name: String,
    imageUrl: String?,
    trailing: String
) {
    val S = LocalStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {

        if (!imageUrl.isNullOrBlank()) {
            GameThumb(
                imageUrl = imageUrl,
                contentDescription = name,
                modifier = Modifier
                    .size(width = 44.dp, height = 56.dp)
                    .clip(RoundedCornerShape(12.dp))
            )
        } else {
            Box(
                modifier = Modifier
                    .size(width = 44.dp, height = 56.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = S.placeholderImg,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }

        Spacer(Modifier.width(10.dp))

        Text(
            text = name,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.weight(1f),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )

        Spacer(Modifier.width(10.dp))

        Text(
            text = trailing,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun GamesMostPlayedCard(
    items: List<br.com.texeratx.boardgametimeapp.data.model.stats.DashboardGameDTO>,
    take: Int = 10
) {
    val S = LocalStrings.current
    Card(shape = RoundedCornerShape(16.dp)) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            items.take(take).forEach { g ->
                GameListRow(
                    name = g.name,
                    imageUrl = g.imageUrl,
                    trailing = S.countFormat(g.count)
                )
            }
        }
    }
}

@Composable
private fun PlayerListRow(
    name: String,
    nickname: String?,
    imageUrl: String?,
    trailing: String,
    trailing2: String? = null
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {

        PlayerAvatar(
            imageUrl = imageUrl,
            contentDescription = name,
            size = 40.dp
        )

        Spacer(Modifier.width(10.dp))

        Column(modifier = Modifier.weight(1f)) {
            val title = nickname?.takeIf { it.isNotBlank() }?.let { "$it • $name" } ?: name
            Text(
                text = title,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (trailing2 != null) {
                Text(
                    text = trailing2,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        Spacer(Modifier.width(10.dp))

        Text(
            text = trailing,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun CategoriesCard(items: List<CategoryStatsDTO>, take: Int = 10) {
    val S = LocalStrings.current
    Card(shape = RoundedCornerShape(16.dp)) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            items.take(take).forEach { cat ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = cat.name,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.weight(1f),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Spacer(Modifier.width(10.dp))
                    Text(
                        text = S.countFormat(cat.count),
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }
    }
}

@Composable
private fun PlayerMinutesCard(items: List<PlayerMinutesDTO>, take: Int = 10) {
    val S = LocalStrings.current
    Card(shape = RoundedCornerShape(16.dp)) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            items.take(take).forEach { p ->
                PlayerListRow(
                    name = p.name,
                    nickname = p.nickname,
                    imageUrl = p.imageUrl,
                    trailing = S.minutesFormat(p.totalMinutes)
                )
            }
        }
    }
}

@Composable
private fun MatchesPerMonthChart(
    matchesPerMonth: Map<Int, Int>,
    modifier: Modifier = Modifier
) {
    val S = LocalStrings.current
    val monthLabels = S.monthsAbbreviations

    val values = (1..12).map { month -> matchesPerMonth[month] ?: 0 }
    val max = (values.maxOrNull() ?: 0).coerceAtLeast(1)

    val barAreaHeight = 120.dp      // só as barras
    val labelsSpacing = 6.dp        // espaço entre barra e textos

    Card(
        shape = RoundedCornerShape(16.dp),
        modifier = modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.Bottom
            ) {
                values.forEachIndexed { index, count ->
                    val ratio = count.toFloat() / max.toFloat()
                    val barHeight = (ratio * barAreaHeight.value).roundToInt().dp

                    Column(
                        modifier = Modifier.weight(1f),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // ✅ Área fixa para barras (não disputa espaço com o texto)
                        Box(
                            modifier = Modifier.height(barAreaHeight),
                            contentAlignment = Alignment.BottomCenter
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(barHeight.coerceAtLeast(2.dp))
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(MaterialTheme.colorScheme.primary)
                            )
                        }

                        Spacer(Modifier.height(labelsSpacing))

                        // ✅ Mês
                        Text(
                            text = monthLabels[index],
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )

                        Text(
                            text = count.toString(),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}
